import React, {
  FC,
  memo,
  MouseEvent,
  MutableRefObject,
  TouchEvent,
  useEffect,
  useRef,
  useState
} from 'react';
import { IPropsPanel, TCoords } from './types';

import Markers from './Markers';

import { getGradient } from '../../../../utils';
import { arraysEqual, shallowEqual } from '../helper';

import { RADIALS_POS } from '../../../../constants';

// Map aliases like 'circle at top center' to 'circle at center top', etc.
// Also handle edge cases like numeric sizes (70, 150%) -> default to center
const normalizeRadialPosition = (modifier: string) => {
  if (typeof modifier !== 'string') return modifier;

  // Handle edge cases:
  // - Pure numeric sizes (70, 150%, 200px, etc.) -> default to center
  // - Size keywords (closest-side, farthest-corner, etc.) -> default to center
  if (
    /^\d+(%|px|em|rem)?$/.test(modifier.trim()) ||
    /^(closest-side|closest-corner|farthest-side|farthest-corner)$/i.test(
      modifier.trim()
    )
  ) {
    return 'circle at center';
  }

  // Handle standard "circle at position" or "ellipse at position" format
  const match = modifier.match(/^(circle|ellipse) at (.+)$/);
  if (!match) {
    // If it doesn't match any known pattern, default to center for radial gradients
    return 'circle at center';
  }

  let pos = match[2]?.trim() || '';
  pos = pos
    .replace(/^top center$/i, 'center top')
    .replace(/^bottom center$/i, 'center bottom')
    .replace(/^left center$/i, 'center left')
    .replace(/^right center$/i, 'center right');
  return `${match[1]} at ${pos}`;
};

const GradientPanel: FC<IPropsPanel> = ({
  color,
  setColor,
  activeColor,
  setActiveColor,
  setInit,
  format = 'rgb',
  showAlpha = true,
  showGradientResult = true,
  showGradientStops = true,
  showGradientMode = true,
  showGradientAngle = true,
  showGradientPosition = true,
  allowAddGradientStops = true
}) => {
  const angleNode = useRef() as MutableRefObject<HTMLDivElement>;

  const { stops, gradient, type, modifier } = color;

  const [radialsPosition, setRadialPosition] = useState(RADIALS_POS);

  const onClickMode = () => {
    setInit(false);
    switch (type) {
      case 'linear': {
        const activePos = radialsPosition.find((item) => item.active);
        setColor({
          ...color,
          modifier: activePos?.css || modifier,
          gradient: `${getGradient(
            'radial',
            stops,
            activePos?.css || modifier,
            format,
            showAlpha
          )}`,
          type: 'radial'
        });
        break;
      }

      case 'radial': {
        setColor({
          ...color,
          gradient: `${getGradient('linear', stops, 180, format, showAlpha)}`,
          type: 'linear'
        });
        break;
      }

      default: {
        break;
      }
    }
  };

  const setActiveRadialPosition = (e: MouseEvent) => {
    setInit(false);
    const target = e.target as HTMLElement;
    const pos = target.getAttribute('data-pos');
    const newRadialsPosition = radialsPosition.map((item) => {
      if (item.pos === pos) {
        return {
          ...item,
          active: true
        };
      }

      return {
        ...item,
        active: false
      };
    });

    setRadialPosition(newRadialsPosition);

    const activePos = newRadialsPosition.find((item) => item.active);
    setColor({
      ...color,
      modifier: activePos?.css || modifier,
      gradient: `${getGradient(
        'radial',
        stops,
        activePos?.css || modifier,
        format,
        showAlpha
      )}`
    });
  };

  const removeListeners = () => {
    window.removeEventListener('mousemove', onDrag);
    window.removeEventListener('mouseup', onDragEnd);
  };

  const removeTouchListeners = () => {
    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('touchend', onTouchEnd);
  };

  const onMouseDown = (e: any) => {
    e.preventDefault();

    setInit(false);

    if (e.button !== 0) return;

    const x = e.clientX;
    const y = e.clientY;
    const shiftKey = e.shiftKey;
    const ctrlKey = e.ctrlKey * 2;

    if (e.target.className !== 'gradient-mode' && type === 'linear') {
      pointMoveTo({
        x,
        y,
        shiftKey,
        ctrlKey
      });

      window.addEventListener('mousemove', onDrag);
      window.addEventListener('mouseup', onDragEnd);
    }
  };

  const onDrag = (e: any) => {
    // Defensive check for event object
    if (!e) {
      console.warn('onDrag called with undefined event object');
      return;
    }

    const x = e.clientX || 0;
    const y = e.clientY || 0;
    const shiftKey = e.shiftKey || false;
    const ctrlKey = (e.ctrlKey || false) * 2;

    pointMoveTo({
      x,
      y,
      shiftKey,
      ctrlKey
    });
  };

  const onDragEnd = (e: any) => {
    const x = e.clientX;
    const y = e.clientY;
    const shiftKey = e.shiftKey;
    const ctrlKey = e.ctrlKey * 2;

    pointMoveTo({
      x,
      y,
      shiftKey,
      ctrlKey
    });

    removeListeners();
  };

  const onTouchStart = (e: TouchEvent) => {
    setInit(false);

    if (e.cancelable) {
      e.preventDefault();
    }

    if (e.touches.length !== 1) {
      return;
    }

    removeTouchListeners();

    const x = e.targetTouches[0].clientX;
    const y = e.targetTouches[0].clientY;
    const shiftKey = false;
    const ctrlKey = 0;

    pointMoveTo({ x, y, shiftKey, ctrlKey });

    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd, { passive: false });
  };

  const onTouchMove = (e: any) => {
    if (e.cancelable) {
      e.preventDefault();
    }

    const x = e.targetTouches[0].clientX;
    const y = e.targetTouches[0].clientY;
    const shiftKey = false;
    const ctrlKey = 0;

    pointMoveTo({
      x,
      y,
      shiftKey,
      ctrlKey
    });
  };

  const onTouchEnd = () => {
    removeTouchListeners();
  };

  const pointMoveTo = (coords: TCoords) => {
    const rect = angleNode && angleNode.current.getBoundingClientRect();

    const boxcx = rect.left + rect.width / 2;
    const boxcy = rect.top + rect.height / 2;
    const radians = Math.atan2(coords.x - boxcx, coords.y - boxcy) - Math.PI;
    const degrees = Math.abs((radians * 180) / Math.PI);

    const div = [1, 2, 4][Number(coords.shiftKey || coords.ctrlKey)];
    const newAngle = degrees - (degrees % (45 / div));

    setColor({
      ...color,
      gradient: `${getGradient(type, stops, newAngle, format, showAlpha)}`,
      modifier: newAngle
    });
  };

  useEffect(() => {
    return () => {
      removeListeners();
      removeTouchListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (type === 'radial') {
      const normalizedModifier = normalizeRadialPosition(String(modifier));
      const activePos = RADIALS_POS.find(
        (item) => item.css === normalizedModifier
      );
      setColor({
        ...color,
        modifier: activePos?.css || normalizedModifier,
        gradient: `${getGradient(
          'radial',
          stops,
          activePos?.css || normalizedModifier,
          format,
          showAlpha
        )}`
      });

      setRadialPosition(
        RADIALS_POS.map((item) => {
          if (item.css === normalizedModifier) {
            return {
              ...item,
              active: true
            };
          }
          return {
            ...item,
            active: false
          };
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifier]);

  return (
    <div className='gradient-interaction'>
      {showGradientResult && (
        <div
          className='gradient-result'
          onMouseDown={showGradientAngle ? onMouseDown : undefined}
          onTouchStart={showGradientAngle ? onTouchStart : undefined}
          style={{ background: gradient }}
        >
          {showGradientMode && (
            <div
              data-mode={type}
              className='gradient-mode'
              onClick={() => onClickMode()}
            />
          )}
          {showGradientAngle && (
            <div
              className='gradient-angle'
              ref={angleNode}
              style={{ visibility: type === 'linear' ? 'visible' : 'hidden' }}
            >
              <div
                style={{
                  transform: `rotate(${
                    typeof modifier === 'number'
                      ? modifier - 90 + 'deg'
                      : modifier
                  })`
                }}
              ></div>
            </div>
          )}
          {showGradientPosition && (
            <div
              className='gradient-pos'
              style={{
                opacity: type === 'radial' ? '1' : '0',
                visibility: type === 'radial' ? 'visible' : 'hidden'
              }}
            >
              {radialsPosition.map((item) => {
                return (
                  <div
                    key={item.pos}
                    data-pos={item.pos}
                    className={item.active ? 'gradient-active' : ''}
                    onClick={(e) => setActiveRadialPosition(e)}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
      {showGradientStops && (
        <Markers
          color={color}
          setColor={setColor}
          activeColor={activeColor}
          setActiveColor={setActiveColor}
          setInit={setInit}
          format={format}
          showAlpha={showAlpha}
          allowAddGradientStops={allowAddGradientStops}
        />
      )}
    </div>
  );
};

const arePropsEqual = (prevProps: any, nextProps: any) => {
  if (
    arraysEqual(prevProps.color.stops, nextProps.color.stops) &&
    prevProps.color.modifier === nextProps.color.modifier &&
    prevProps.color.type === nextProps.color.type &&
    shallowEqual(prevProps.activeColor, nextProps.activeColor)
  ) {
    return true;
  }

  return false;
};

export default memo(GradientPanel, arePropsEqual);
